import { useContext } from "react"
import { Link, useParams, useNavigate, useLocation } from "react-router-dom"
import { useQuery, useMutation } from "@apollo/client"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import { LoaderMedium } from "../../../../components/Loaders"
import AssessmentBlock from "../../../Assessments/AssessmentBlock/AssessmentBlock"
import { trimTextValue } from "../../../../components/FieldTypes/helpers/trimTextValue"

// Context
import { UserContext } from "../../../../context/userContext"

// Prime
import { InputText as Input } from "primereact/inputtext"
import { Button } from "primereact/button"
import { ToggleButton } from "primereact/togglebutton"

// Styles
import styles from "../../../../styles/Form.module.css"

import { ADD_BLOCK, GET_PARENT_BLOCK, GET_BLOCKS_LIBRARY } from "../../queries"

import "../../PickList.css"

export default function BlockAssessmentAdd() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const location = useLocation()

  const { idBlock } = useParams()

  const [addBlock] = useMutation(ADD_BLOCK)

  const isAddPath = location.pathname.includes("/block/assessment/add/")
  const isCustomizePath = location.pathname.includes(
    "/block/assessment/customize/"
  )

  const handleBlockName = () => {
    if (isAddPath) {
      return "session__assessment____custom"
    }
    if (isCustomizePath) {
      return "session__assessment"
    }
    return "session__assessment"
  }

  const BLOCK_NAME = handleBlockName()

  const { data, loading } = useQuery(GET_PARENT_BLOCK, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      idBlock: idBlock,
      type: "system",
    },
  })

  if (loading) return <LoaderMedium />

  const newBlock = Yup.object().shape({
    block_title: Yup.string(),
  })

  return (
    <div>
      <h1>
        {isAddPath ? "Add New Assessment Block" : "Customize Assessment Block"}
      </h1>
      <br />
      <Link
        to="/library/blocks"
        style={{ marginBottom: 30, display: "inline-block" }}
        className="btn"
      >
        Back to Library
      </Link>
      <Formik
        initialValues={{
          block_name: BLOCK_NAME,
          block_title: data.currentBlock.block_title,
          data_propogation:
            data.currentBlock.configuration.behavior.data_propagation,
          block_required: data.currentBlock.configuration.system_required,
          assessments: [],
        }}
        validationSchema={newBlock}
        onSubmit={async (values) => {
          let assessmentValue = values.assessments
            ? values.assessments.map((assessment) => {
              return {
                assessment_id: assessment.assessment_id,
                required: assessment.required,
              }
            })
            : []

          await addBlock({
            variables: {
              organization_id: userCtx.session.user.organization_id,
              enterprise_id: userCtx.session.user.enterprise_id,
              created_by: userCtx.session.user.staff_id,
              updated_by: userCtx.session.user.staff_id,
              block_parent: idBlock,
              fields: JSON.stringify([
                {
                  idField: "assessments",
                  field_name: "assessment",
                },
              ]),
              block_name: values.block_name,
              block_title: values.block_title,
              configuration: JSON.stringify({
                allow_custom_config: false,
                behavior: {
                  data_propagation: false,
                },
                rules: {
                  programs: [],
                  assessments: assessmentValue,
                },
                system_required: values.block_required,
              }),
            },
            refetchQueries: [
              {
                query: GET_BLOCKS_LIBRARY,
                variables: {
                  staff_id: userCtx.session.user.staff_id,
                  organization_id: userCtx.session.user.organization_id,
                  enterprise_id: userCtx.session.user.enterprise_id,
                },
              },
            ],
          })
          navigate("/library/blocks")
        }}
      >
        {(formik) => {
          return (
            <Form className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>Current Block Name:</label>
                <Input
                  name="block_name"
                  value={BLOCK_NAME}
                  disabled
                  className={styles.value}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>New Block Name:</label>
                <Input
                  name="block_title"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={formik.handleBlur}
                  value={formik.values.block_title}
                  className={styles.value}
                />
                {formik.touched.block_title && formik.errors.block_title && (
                  <div className={styles.errFieldMsg}>
                    {formik.errors.block_title}
                  </div>
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Is required:</label>
                <ToggleButton
                  name="block_required"
                  checked={formik.values.block_required}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onIcon="pi pi-check"
                  offIcon="pi pi-times"
                  aria-label="block_required"
                  className={styles.value}
                />
              </div>

              <AssessmentBlock
                assessmentConfig={formik.values.assessments}
                onSubmitAssessments={(newAssessmentConfig) => {
                  formik.setFieldValue("assessments", newAssessmentConfig)
                }}
                adminMode={true}
              />

              <Button
                style={{ marginTop: 15 }}
                label="Submit"
                icon="pi pi-check"
                loading={formik.isSubmitting}
                disabled={!formik.isValid}
                type="submit"
                className={styles.btnSubmit}
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
